import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Play, RotateCcw, Crosshair, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAcademyStore } from '../../store/academyStore';
import { useSfx } from '../../store/sfxStore';

interface Asteroid {
    id: number;
    text: string;
    y: number;
    x: number;
}

const WORDS = [
    "ERROR:404", "192.168.1.1", "admin@corp.com", "user_123", "password123",
    "[DEBUG]", "token=abcdef", "session_id=99", "Failed login", "200_OK"
];

export const RegexAsteroids = () => {
    const { play } = useSfx();
    const { completeModule } = useAcademyStore();
    
    const [gameState, setGameState] = useState<'idle' | 'playing' | 'gameOver'>('idle');
    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(3);
    const [inputRegex, setInputRegex] = useState('^ERROR');
    const [asteroids, setAsteroids] = useState<Asteroid[]>([]);
    
    // Use a ref for the animation loop
    const requestRef = useRef<number>();
    const lastAsteroidTime = useRef<number>(0);

    const checkCollisions = (currentRegex: string) => {
        try {
            const regex = new RegExp(currentRegex);
            setAsteroids(prev => {
                const survivors = prev.filter(ast => !regex.test(ast.text));
                const destroyed = prev.length - survivors.length;
                if (destroyed > 0) {
                    play('success');
                    setScore(s => s + (destroyed * 100));
                }
                return survivors;
            });
        } catch (e) {
            // Invalid regex, do nothing
        }
    };

    const fireLaser = (e: React.FormEvent) => {
        e.preventDefault();
        play('click');
        checkCollisions(inputRegex);
        setInputRegex(''); // Clear after fire
    };

    const startGame = () => {
        play('click');
        setScore(0);
        setLives(3);
        setAsteroids([]);
        setInputRegex('');
        setGameState('playing');
        lastAsteroidTime.current = Date.now();
    };

    useEffect(() => {
        if (gameState !== 'playing') return;

        const loop = () => {
            const now = Date.now();
            
            // Spawn new asteroid every 2 seconds
            if (now - lastAsteroidTime.current > 2000) {
                setAsteroids(prev => [
                    ...prev,
                    {
                        id: now,
                        text: WORDS[Math.floor(Math.random() * WORDS.length)],
                        y: 0,
                        x: 10 + Math.random() * 80 // 10% to 90% width
                    }
                ]);
                lastAsteroidTime.current = now;
            }

            // Move asteroids down
            setAsteroids(prev => {
                let lostLife = false;
                const active = prev.map(a => ({ ...a, y: a.y + 0.3 })).filter(a => {
                    if (a.y > 100) {
                        lostLife = true;
                        return false;
                    }
                    return true;
                });

                if (lostLife) {
                    play('fail');
                    setLives(l => {
                        const newLives = l - 1;
                        if (newLives <= 0) {
                            setGameState('gameOver');
                        }
                        return newLives;
                    });
                }
                return active;
            });

            requestRef.current = requestAnimationFrame(loop);
        };

        requestRef.current = requestAnimationFrame(loop);

        return () => {
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
        };
    }, [gameState]);

    useEffect(() => {
        if (gameState === 'gameOver' && score > 500) {
            completeModule('regex-asteroids-game', score);
        }
    }, [gameState]);

    return (
        <div className="min-h-screen bg-black font-mono text-green-500 flex flex-col pt-20 overflow-hidden relative">
            {/* Retro grid background */}
            <div className="absolute inset-0 bg-[linear-gradient(transparent_95%,rgba(0,255,0,0.2)_100%)] bg-[length:100%_40px] pointer-events-none" />

            {/* Header */}
            <div className="bg-black/80 border-b-2 border-green-500/50 p-4 relative z-10">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <Link to="/games" onClick={() => play('click')} className="flex items-center gap-2 text-green-600 hover:text-green-400 font-bold uppercase tracking-widest">
                        <ArrowLeft size={20} /> Abort Mission
                    </Link>
                    <div className="flex items-center gap-8">
                        <div className="text-xl font-bold font-mono">
                            SCORE: <span className="text-white drop-shadow-[0_0_8px_#fff]">{score.toString().padStart(5, '0')}</span>
                        </div>
                        <div className="flex gap-2">
                            {[...Array(3)].map((_, i) => (
                                <motion.div 
                                    key={i} 
                                    animate={{ opacity: i < lives ? 1 : 0.2 }}
                                    className="w-5 h-5 bg-green-500 rounded-sm shadow-[0_0_10px_#22c55e]" 
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Game Area */}
            <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full relative">
                
                {gameState === 'idle' && (
                    <div className="flex-1 flex items-center justify-center">
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-black border border-green-500/50 p-12 text-center max-w-lg w-full relative overflow-hidden shadow-[0_0_30px_rgba(34,197,94,0.1)]">
                            <Crosshair className="w-24 h-24 text-green-500 mx-auto mb-6 opacity-80" />
                            <h1 className="text-4xl font-black mb-4 tracking-widest text-white drop-shadow-[0_0_10px_rgba(34,197,94,1)]">REGEX ASTEROIDS</h1>
                            <p className="text-green-600 mb-8 font-bold leading-relaxed uppercase max-w-sm mx-auto">
                                Type regular expressions to destroy matching server logs before they crash the system.
                            </p>
                            <button onClick={startGame} className="w-full py-4 border-2 border-green-500 hover:bg-green-500 hover:text-black text-green-500 font-bold flex items-center justify-center gap-2 text-xl transition-all shadow-[0_0_15px_rgba(34,197,94,0.3)]">
                                <Play fill="currentColor" /> INITIATE SYSTEM
                            </button>
                        </motion.div>
                    </div>
                )}

                {gameState === 'playing' && (
                    <div className="flex-1 relative w-full h-full border-x border-green-900/30">
                        {asteroids.map(ast => (
                            <div 
                                key={ast.id}
                                className="absolute text-xl font-bold text-red-400 whitespace-nowrap drop-shadow-[0_0_8px_rgba(248,113,113,0.8)]"
                                style={{ 
                                    top: `${ast.y}%`, 
                                    left: `${ast.x}%`, 
                                    transform: 'translateX(-50%)' 
                                }}
                            >
                                <span className="opacity-50">[LOG]</span> {ast.text}
                            </div>
                        ))}
                    </div>
                )}

                {gameState === 'playing' && (
                    <form onSubmit={fireLaser} className="bg-black border-t-2 border-green-500/50 p-6 relative z-10 flex gap-4">
                        <div className="flex-1 relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-green-600 font-bold text-xl">/</span>
                            <input
                                autoFocus
                                type="text"
                                value={inputRegex}
                                onChange={e => setInputRegex(e.target.value)}
                                className="w-full bg-black border border-green-700 p-4 pl-8 pr-8 font-mono text-2xl outline-none focus:border-green-400 text-white shadow-[inset_0_0_20px_rgba(34,197,94,0.1)] placeholder-green-900 focus:shadow-[0_0_15px_rgba(34,197,94,0.3)] uppercase"
                                placeholder="..."
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-green-600 font-bold text-xl">/g</span>
                        </div>
                        <button type="submit" className="px-10 border-2 border-green-500 hover:bg-green-500 hover:text-black text-green-500 font-black tracking-widest text-xl transition-all flex items-center gap-2">
                            <Target /> FIRE
                        </button>
                    </form>
                )}

                {gameState === 'gameOver' && (
                    <div className="flex-1 flex items-center justify-center relative z-10">
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-black border-2 border-red-500 p-12 text-center max-w-lg w-full shadow-[0_0_50px_rgba(239,68,68,0.2)]">
                            <h2 className="text-4xl font-black mb-2 text-red-500 tracking-widest drop-shadow-[0_0_10px_rgba(239,68,68,0.8)]">SYSTEM CRITICAL</h2>
                            <p className="text-red-400 mb-6 uppercase text-sm font-bold">Base Defenses Breached</p>
                            
                            <div className="text-6xl font-black text-white my-8 drop-shadow-[0_0_15px_rgba(255,255,255,0.8)]">
                                {score}
                            </div>
                            
                            <button onClick={startGame} className="w-full py-4 border-2 border-green-500 hover:bg-green-500 hover:text-black text-green-500 font-bold flex items-center justify-center gap-2 text-xl tracking-widest transition-all mb-4">
                                <RotateCcw size={20} /> REBOOT SYSTEM
                            </button>
                            <Link to="/games" className="w-full py-4 border border-slate-700 hover:border-slate-500 text-slate-400 hover:text-white font-bold flex items-center justify-center gap-2 text-xl uppercase tracking-widest transition-all block">
                                ABORT
                            </Link>
                        </motion.div>
                    </div>
                )}

            </div>
        </div>
    );
};
